import React, { useState, useId } from 'react';

const cx = (...args) => args.filter(Boolean).join(' ');

const sizeToHeaderPad = (size) =>
  size === 'sm' ? 'px-3 py-2.5' : size === 'lg' ? 'px-6 py-5' : 'px-5 py-3.5';

const sizeToContentPad = (size) =>
  size === 'sm' ? 'p-3' : size === 'lg' ? 'p-6' : 'p-5';

const numberPadToClass = (n) => {
  const v = Math.max(0, Math.min(8, Number(n)));
  return `p-${v}`;
};

const variantClassMap = {
  plain: '',
  card: 'bg-white rounded-xl border border-gray-200/80 shadow-sm ring-1 ring-black/[0.04] overflow-hidden',
  bordered: 'bg-white rounded-xl border border-gray-200 overflow-hidden',
  shadow: 'bg-white rounded-xl shadow-lg shadow-gray-200/70 ring-1 ring-black/[0.04] overflow-hidden',
  dashed: 'bg-white rounded-xl border border-dashed border-gray-300 overflow-hidden',
  subtle: 'rounded-xl bg-gradient-to-b from-gray-50 to-gray-50/60 border border-gray-100 overflow-hidden',
};

const ChevronDownIcon = ({ className = 'h-5 w-5 text-gray-400' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M6 8l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SkeletonLines = ({ lines = 3 }) => (
  <div className="animate-pulse space-y-3">
    {Array.from({ length: lines }).map((_, i) => (
      <div
        key={i}
        className={cx(
          'h-3.5 rounded-full bg-gray-200',
          i === 0 ? 'w-2/3' : i === lines - 1 ? 'w-1/2' : 'w-full'
        )}
      />
    ))}
  </div>
);

export default function SectionContainer({
  variant = 'card',
  size = 'md',
  fullWidth = true,
  title,
  description,
  actions,
  header,
  footer,
  collapsible = false,
  defaultExpanded = true,
  expanded,
  onExpandedChange,
  loading = false,
  empty = false,
  emptyContent = 'Nothing to show here.',
  padding = true,
  className = '',
  style,
  children,
}) {
  const isControlled = typeof expanded === 'boolean';
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = isControlled ? expanded : internalExpanded;

  const contentId = useId();
  const headerPad = sizeToHeaderPad(size);
  const contentPad =
    padding === true
      ? sizeToContentPad(size)
      : padding === false
      ? ''
      : numberPadToClass(padding);

  const onToggle = () => {
    const next = !isExpanded;
    if (!isControlled) setInternalExpanded(next);
    onExpandedChange?.(next);
  };

  const onHeaderKeyDown = (e) => {
    if (!collapsible) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  const headerBaseClasses = cx(
    'flex items-center justify-between border-b border-gray-100',
    'bg-gradient-to-b from-gray-50/70 to-transparent',
    headerPad
  );

  const wrapperClasses = cx(
    fullWidth && 'w-full',
    variantClassMap[variant] || '',
    className
  );

  const renderHeader = () => {
    if (!header && !title && !actions && !description && !collapsible) return null;

    if (header) {
      return (
        <div className={headerBaseClasses}>
          <div className="min-w-0">{header}</div>
          <div className="flex items-center gap-2">
            {actions}
            {collapsible && (
              <button
                type="button"
                aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
                onClick={onToggle}
                className="inline-flex items-center justify-center rounded-md p-1 hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
              >
                <ChevronDownIcon
                  className={cx(
                    'h-4 w-4 text-gray-400 transition-transform duration-200',
                    isExpanded ? '' : '-rotate-90'
                  )}
                />
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        className={cx(
          headerBaseClasses,
          collapsible && 'cursor-pointer select-none hover:bg-gray-50/90 transition-colors duration-150'
        )}
        role={collapsible ? 'button' : undefined}
        aria-expanded={collapsible ? isExpanded : undefined}
        aria-controls={collapsible ? contentId : undefined}
        onClick={collapsible ? onToggle : undefined}
        onKeyDown={collapsible ? onHeaderKeyDown : undefined}
      >
        <div className="min-w-0">
          {title && (
            <div className={cx(
              'font-semibold tracking-tight truncate text-gray-900',
              size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base'
            )}>
              {title}
            </div>
          )}
          {description && (
            <div className="mt-0.5 text-sm text-gray-500 leading-relaxed line-clamp-2">
              {description}
            </div>
          )}
        </div>
        <div className="ml-4 flex items-center gap-2 shrink-0">
          {actions}
          {collapsible && (
            <ChevronDownIcon
              className={cx(
                'h-4 w-4 text-gray-400 transition-transform duration-200',
                isExpanded ? '' : '-rotate-90'
              )}
            />
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!isExpanded) return null;

    // When the outer <section> is styled as a flex/grid container, the children
    // are NOT direct flex items (they're inside the inner <div>). Forward the
    // layout properties so the inner div acts as the real flex/grid container.
    let layoutStyle;
    if (style && (style.display === 'flex' || style.display === 'grid')) {
      layoutStyle = {
        display: style.display,
        flexDirection: style.flexDirection,
        flexWrap: style.flexWrap,
        alignItems: style.alignItems,
        justifyContent: style.justifyContent,
        gap: style.gap,
        gridTemplateColumns: style.gridTemplateColumns,
      };
    }

    return (
      <div id={contentId} className={contentPad} style={layoutStyle}>
        {loading ? (
          <div className="py-8">
            <SkeletonLines lines={3} />
          </div>
        ) : empty ? (
          <div className="py-10 text-center text-sm text-gray-400">{emptyContent}</div>
        ) : (
          children
        )}
      </div>
    );
  };

  const renderFooter = () => {
    if (!footer || !isExpanded) return null;
    return (
      <div className={cx(
        'border-t border-gray-100 bg-gradient-to-b from-gray-50/40 to-transparent',
        sizeToHeaderPad(size)
      )}>
        {footer}
      </div>
    );
  };

  return (
    <section className={wrapperClasses} style={style}>
      {renderHeader()}
      {renderContent()}
      {renderFooter()}
    </section>
  );
}
